import { useState, useEffect } from 'react';
import { formatDistanceToNow } from '@/lib/utils';

interface RelativeTimeProps {
  date: string | Date;
  className?: string;
}

/** How often to re-render based on age of the timestamp */
function refreshInterval(date: Date): number {
  const ageMs = Date.now() - date.getTime();
  if (ageMs < 60_000) return 10_000;       // < 1m → every 10s
  if (ageMs < 3_600_000) return 30_000;     // < 1h → every 30s
  if (ageMs < 86_400_000) return 60_000;    // < 1d → every 60s
  return 0;                                  // older → no refresh
}

/**
 * GROW-005 / UI-091: Live-updating relative timestamp.
 * Renders a `<time>` element with:
 * - `datetime` attribute for accessibility & SEO
 * - `title` showing the full locale date on hover
 * - Auto-refreshing display text ("2h ago", "just now", etc.)
 */
export function RelativeTime({ date, className }: RelativeTimeProps) {
  const d = date instanceof Date ? date : new Date(date);
  const [, setTick] = useState(0);

  useEffect(() => {
    const ms = refreshInterval(d);
    if (ms === 0) return;
    const id = setInterval(() => setTick((t) => t + 1), ms);
    return () => clearInterval(id);
  }, [d.getTime()]);

  return (
    <time
      dateTime={d.toISOString()}
      title={d.toLocaleDateString('en-IN', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })}
      className={className}
    >
      {formatDistanceToNow(d)}
    </time>
  );
}
